import * as R from "ramda";

import {
  composeView,
  CourseComponentView,
  courseComponentViews,
  courseUnitViews,
  fullCourseViews,
} from "../../../../utils/courseViews";
import { PreferredLocale } from "../../../../utils/locales";
import {
  CourseUnitView,
  CourseView,
  DefaultNames,
  IAssessment,
  ICourseComponent,
  ICourseInstruction,
  ICourseInstructionContentPart,
  ICourseUnit,
  IFullCourse,
  isAssessment,
  isInstruction,
  isUnit,
  Slide,
} from "../../types";
import { IClientPatch, IPatchAction } from "./";

const unitsToPosition = (position: string, unit: ICourseComponent) => `${position}.children.${unit.id}`;

export function findComponent(course: IFullCourse, unitPath: CourseUnitView[], component?: Nullable<Slide>): string {
  const start = "cc.root";
  const units: ICourseUnit[] = unitPath.map(view => view(course)) as ICourseUnit[];

  if (R.isNil(component)) {
    return units.reduce(unitsToPosition, start);
  }

  if (isAssessment(component)) {
    return `${units.reduce(unitsToPosition, start)}.test`;
  }

  return [...units, component].reduce(unitsToPosition, start);
}

export function findComponentPath(course: IFullCourse, position: string): CourseComponentView[] {
  const pathList = position.split(".");
  const findSubUnitIndex = (id: number, comps: ICourseComponent[]) =>
    R.findIndex((comp: ICourseComponent) => comp.id === id, comps);

  return pathList.reduce((views: CourseComponentView[], item, n) => {
    if (R.contains(item, ["cc", "root"])) {
      return views;
    }

    const currentView = R.last(views);
    const id = parseInt(item, 10);
    const isChild = pathList[n - 1] === "children";

    // Current item represents a type (e.g. "children", "parts") of component rather
    // than an individual component OR is a toplevel setting value (e.g. "publications", "setting")
    if (!Number.isInteger(id) || (R.isNil(currentView) && !isChild)) {
      return views;
    }

    // Current item is a top-level unit (children of root)
    if (R.isNil(currentView) && isChild) {
      const units = fullCourseViews.units(course) as ICourseUnit[];
      const targetIndex = findSubUnitIndex(id, units);
      const targetUnitView = (sunits: ICourseUnit[]) => sunits[targetIndex] || null;
      return [composeView(targetUnitView, fullCourseViews.units)];
    }

    // Current item is either a subunit or instructional component of a unit
    if (!R.isNil(currentView) && isChild) {
      const currentUnit = currentView(course) as ICourseUnit;
      const subUnits = courseUnitViews.components(currentUnit) as ICourseComponent[];
      const targetIndex = findSubUnitIndex(id, subUnits);
      const targetSubUnitView: CourseView<ICourseUnit, ICourseComponent[]> = (subUnitArgs: ICourseComponent[]) =>
        (subUnitArgs[targetIndex] as ICourseUnit) || null;
      return [
        ...views,
        composeView(
          targetSubUnitView,
          composeView(
            courseUnitViews.components as CourseView<ICourseComponent[], ICourseUnit>,
            R.last(views) as CourseUnitView
          )
        ) as CourseComponentView,
      ];
    }

    // All other cases
    return views;
  }, []);
}

export function getPatchTarget(path: CourseComponentView[], pos: string[], course: IFullCourse) {
  const targetView = R.last(path);
  const deleteToplevelUnit = R.last(pos) === "deleted" && path.length === 1;
  const target = !!targetView && !deleteToplevelUnit && (targetView(course) as ICourseComponent);
  return target;
}

export function targetTitle(
  course: IFullCourse,
  target: false | ICourseComponent,
  path: CourseComponentView[],
  change: IClientPatch<IPatchAction>
) {
  let title;
  const masterLocale = fullCourseViews.masterLocale(course) as PreferredLocale;

  if (target === false) {
    // Target is a course-level setting, not a unit or instructional component
    const courseTitle = fullCourseViews.masterLocaleTitle(course) || DefaultNames.Course;
    title = `course '${courseTitle}'`;
  } else if (isUnit(target)) {
    const compTitle = courseComponentViews.localizedTitle(masterLocale)(target) || DefaultNames.Unit;
    title = `unit '${compTitle}'`;
  } else if (isInstruction(target)) {
    // If target is an instructional component, then path.length >= 2 since
    // instructional components must be inside a unit so path should be
    // [..., unitView, instructionView].
    const unitView = path[path.length - 2];
    const unit = unitView && (unitView(course) as ICourseUnit);
    const unitTitle = courseComponentViews.localizedTitle(masterLocale)(unit) || DefaultNames.Unit;
    title = `unit '${unitTitle}'`;
  }

  if (!R.isNil(change.undoVersionId)) {
    title = `${title} (undo of change ${change.undoVersionId})`;
  }
  return title;
}

export function findPartPosition(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ICourseInstructionContentPart
): string {
  return `${findComponent(course, unitPath, comp)}.parts.${part.id}`;
}

export function findTitlePosition(
  course: IFullCourse,
  locale: PreferredLocale,
  component: ICourseComponent | null = null,
  unitPath: Array<CourseView<ICourseUnit, IFullCourse>> = []
): string {
  if (R.isNil(component)) {
    const title = R.find(({ locale: loc }) => loc === locale, course.state.meta.mLTitle.elems);
    if (title) {
      return `meta.mLTitle.${title.id}.value.text`;
    }
    return `meta.mLTitle`;
  }

  const start = "cc.root";
  const units: ICourseUnit[] = unitPath.map(view => view(course)) as ICourseUnit[];

  const componentPosition = [...units, component].reduce(unitsToPosition, start);

  if (isUnit(component)) {
    const title = R.find(({ locale: loc }) => loc === locale, component.content.title.elems);
    if (title) {
      return `${componentPosition}.title.${title.id}.value.text`;
    }
    return `${componentPosition}.title`;
  } else {
    const name = R.find(({ locale: loc }) => loc === locale, component.content.name.elems);
    if (name) {
      return `${componentPosition}.name.${name.id}.value.text`;
    }
    return `${componentPosition}.name`;
  }
}

export function findNextId(course: IFullCourse, unitPath: Array<CourseView<ICourseUnit, IFullCourse>>): number | null {
  const view = R.last(unitPath);
  if (R.isNil(view)) {
    return fullCourseViews.nextChildId(course);
  }

  const parentUnit = view(course);
  return courseUnitViews.nextChildId(parentUnit);
}

export function findLastChildComponentIndex(
  course: IFullCourse,
  unitPath: Array<CourseView<ICourseUnit, IFullCourse>>
): number {
  const unitView = R.last(unitPath);

  if (!R.isNil(unitView)) {
    const componentList = courseUnitViews.components(unitView(course)) || [];
    return componentList.length - 1;
  }

  const unitList = fullCourseViews.units(course) || [];
  return unitList.length - 1;
}

export const getPositionedValues = (
  course: IFullCourse,
  instruction: ICourseInstruction | IAssessment,
  unitPath: CourseUnitView[],
  itemId?: number
): {
  insertedElementIndex: number;
  nextId: number;
  position: string;
} => {
  if (isAssessment(instruction)) {
    return {
      insertedElementIndex:
        itemId || itemId === 0
          ? instruction.questionBank.elems.map(q => q.id).indexOf(itemId)
          : instruction.questionBank.elems.length,
      nextId: instruction.questionBank.nextId,
      position: `${findComponent(course, unitPath)}.test.questionBank`,
    };
  }
  return {
    insertedElementIndex:
      itemId || itemId === 0
        ? instruction.content.parts.elems.map(p => p.id).indexOf(itemId)
        : instruction.content.parts.elems.length,
    nextId: instruction.content.parts.nextId,
    position: `${findComponent(course, unitPath, instruction)}.parts`,
  };
};
